// Iraqi Arabic dictionary (not MSA). Register: confident, plain-spoken Iraqi
// tech company voice, the way Datacode would actually talk to a client, not
// stiff formal Arabic and not street slang.
import type { Dictionary } from './en';

export const ar: Dictionary = {
  common: {
    skipToMainContent: 'الانتقال إلى المحتوى الرئيسي',
  },
  meta: {
    homeTitle: 'Datacode | دمج الذكاء الاصطناعي، حلول LLM، ووكالة تطوير الويب',
    homeDescription:
      'Datacode وكالة رقمية بأربيل، كوردستان. نصمم ونطور تطبيقات ويب، حلول دمج الذكاء الاصطناعي، ومنتجات موبايل، من أول سطر كود لين مرحلة الإطلاق.',
  },
  header: {
    nav: {
      home: 'الرئيسية',
      portfolio: 'أعمالنا',
      products: 'منتجاتنا',
      about: 'من نحن',
      contact: 'تواصل معنا',
      careers: 'وظائف',
    },
    toggleMenuAriaLabel: 'فتح/إغلاق القائمة',
    email: 'info@datacode.app',
    socials: {
      instagramAriaLabel: 'Instagram',
      linkedinAriaLabel: 'LinkedIn',
      twitterAriaLabel: 'Twitter/X',
    },
    tagline: 'Datacode',
    languageSwitcher: {
      ariaLabel: 'اختر اللغة',
      en: 'EN',
      ar: 'عربي',
      ckb: 'کوردی',
    },
  },
  footer: {
    eyebrow: 'ابدأ مشروعك',
    // Two-line pitch. Keep as two entries since it renders as two lines
    // ("Have something to build?" then a dimmer sub-line).
    pitchLine1: 'عندك شي تريد تبنيه؟',
    pitchLine2: 'احجي مباشرة مع الناس اللي رح يبنونه.',
    email: 'info@datacode.app',
    phone: '+964-751-449-1008',
    address: {
      line1: 'شارع القرية الإنكليزية',
      line2: 'أربيل، إقليم كوردستان، العراق',
    },
    columns: {
      studio: 'الاستوديو',
      services: 'خدماتنا',
      products: 'منتجاتنا',
      contact: 'تواصل معنا',
    },
    studioLinks: {
      home: 'الرئيسية',
      portfolio: 'أعمالنا',
      about: 'من نحن',
      articles: 'مقالات',
      careers: 'وظائف',
    },
    serviceLinks: {
      webDesign: 'تصميم وتطوير مواقع الويب',
      mobileApp: 'تطوير تطبيقات الموبايل',
      aiIntegration: 'دمج الذكاء الاصطناعي',
      uiUx: 'تصميم UI/UX',
      allServices: 'كل الخدمات',
    },
    productLinks: {
      dilaCrm: 'DilaCRM',
      dilaErp: 'DilaERP',
      dilaMenu: 'dila.menu',
      allProducts: 'كل المنتجات',
    },
    legalLinks: {
      support: 'الدعم',
      terms: 'الشروط',
      privacy: 'الخصوصية',
    },
    copyright: 'Datacode',
    erbilLabel: 'أربيل',
    backToTop: 'للأعلى',
  },
  hero: {
    // Rendered letter-by-letter, kept as a single word so the split('')
    // animation still works.
    title: 'DATACODE',
    subtitleMobile:
      'نصمم ونطور تطبيقات ويب، أدوات ذكاء اصطناعي، ومنتجات موبايل، من أول سطر كود لين مرحلة الإطلاق. نبنيها بكوردستان ونوصلها لكل العالم.',
    subtitleDesktopLine1: 'نصمم ونطور تطبيقات ويب، أدوات ذكاء اصطناعي، ومنتجات',
    subtitleDesktopLine2: 'موبايل، من أول سطر كود لين مرحلة الإطلاق.',
    subtitleDesktopLine3: 'نبنيها بكوردستان، ونوصلها لكل العالم.',
    subtitleRevealLine1: 'بدون قوالب جاهزة. بدون وسطاء. بدون تسليم الشغل لفريق مبتدئ.',
    subtitleRevealLine2: 'تشتغل مباشرة مع الناس اللي يكتبون الكود.',
    subtitleRevealLine3: 'هاي بالضبط الفكرة.',
    scrollToExplore: 'مرر للأسفل للاستكشاف',
    statusLine: {
      location: 'أربيل، المقر الرئيسي',
      systemsLive: 'الأنظمة تعمل',
    },
  },
  portfolio: {
    // Visually hidden heading above the featured-work gallery.
    sectionHeading: 'أعمال مختارة',
    loading: 'جاري تحميل المشاريع...',
    empty: 'لا توجد مشاريع متاحة حالياً.',
    getInTouch: 'تواصل معنا',
    imageUnavailable: 'الصورة غير متوفرة',
    viewLabel: 'عرض',
  },
  statement: {
    row1: {
      webApps: 'تطبيقات ويب',
      aiTools: 'أدوات ذكاء اصطناعي',
      mobile: 'موبايل',
      platforms: 'منصات',
      production: 'إنتاج',
    },
    row2: {
      design: 'تصميم',
      build: 'بناء',
      ship: 'إطلاق',
      scale: 'توسّع',
      support: 'دعم',
    },
    mockupAlt: 'واجهة تطبيقات متعددة',
  },
  glassStack: {
    // Visually hidden heading; the eyebrow below it is the visible label.
    sectionHeading: 'كيف نبني',
    eyebrow: 'كيف نبني',
    layers: {
      infrastructure: {
        title: 'البنية التحتية والأمان',
        desc: 'نتكفل بالنشر والمراقبة والأمان من أول يوم، هذي الطبقة الأساس اللي يبنى عليها كل شي ثاني.',
      },
      ai: {
        title: 'الذكاء الاصطناعي والوكلاء',
        desc: 'خصائص ذكية وأتمتة مدمجة بمنتجات حقيقية، مساعدين نطلقهم فعلاً، مو بس عرض تجريبي.',
      },
      web: {
        title: 'منصات الويب',
        desc: 'مواقع ومنصات تفتح بسرعة، تترتب زين بمحركات البحث، وتتحمل الضغط الحقيقي.',
      },
      mobile: {
        title: 'تطبيقات الموبايل',
        desc: 'تطبيقات مصقولة لـ iPhone وAndroid، يطلعها فريق وحد على المتجرين.',
      },
      design: {
        title: 'التصميم والتفاعل',
        desc: 'واجهات نصممها بأنفسنا بالداخل، الحركة، الهوية، وتجربة المستخدم نقررها قبل لا نكتب أي سطر كود.',
      },
    },
  },
  skills: {
    // Visually hidden heading; the eyebrow below it is the visible label.
    sectionHeading: 'مجالات خبرتنا',
    eyebrow: 'مجالات خبرتنا',
    fallback: {
      webDesign: 'تصميم مواقع',
      mobileAppDevelopment: 'تطوير تطبيقات موبايل',
      uiUxDesign: 'تصميم UI/UX',
      aiEngineering: 'هندسة الذكاء الاصطناعي',
      aiAgentsIntegrations: 'وكلاء الذكاء الاصطناعي والدمج',
      techDueDiligence: 'التدقيق التقني',
      fractionalCto: 'CTO بدوام جزئي وبناء الفرق',
      mvpMobileWebsite: 'MVP موبايل/ويب',
    },
  },
  contact: {
    eyebrow: 'ابدأ مشروعك',
    title: 'خلّينا نحچي',
    subtitle:
      'خبّرنا شنو تريد تبني. راح تسمع رد من الناس اللي فعلاً يشتغلون عليه، مو من قسم المبيعات.',
    email: 'info@datacode.app',
    phone: '+964-751-449-1008',
    labels: {
      email: 'البريد الإلكتروني',
      location: 'الموقع',
      phone: 'الهاتف',
    },
    location: {
      line1: 'أربيل، إقليم كوردستان',
      line2: 'العراق',
    },
    form: {
      nameLabel: 'الاسم',
      emailLabel: 'البريد الإلكتروني',
      messageLabel: 'الرسالة',
      sending: 'جاري الإرسال...',
      send: 'إرسال الرسالة',
      success: 'شكراً! تم إرسال رسالتك، رح نتواصل وياك قريب.',
      error: 'صار خطأ. راسلنا عبر info@datacode.app',
      validation: {
        nameMin: 'الاسم لازم يكون حرفين على الأقل',
        emailInvalid: 'الرجاء إدخال بريد إلكتروني صحيح',
        messageMin: 'الرسالة لازم تكون 10 أحرف على الأقل',
      },
    },
  },
  ourStudio: {
    eyebrow: 'التصميم والتفاعل',
    // Rendered letter-by-letter, kept as a single phrase so the split('')
    // animation still works.
    title: 'استوديونا',
  },
  careers: {
    index: {
      eyebrow: 'وظائف',
      heading: 'الوظائف المتاحة',
      subtitle: 'نوظف محلياً بأربيل، كوردستان، ناس تكدر تشتغل لحالها وتتحمل مسؤولية شغلها، مو بس تعمر مكان فاضي.',
      empty: 'ماكو وظائف شاغرة هسة، رجع تشوفنا بعدين.',
      metaTitle: 'وظائف',
      metaDescription: 'وظائف شاغرة بداتاكود: أخصائي مبيعات وأخصائي علاقات عامة ومبيعات، نوظف محلياً بأربيل، كوردستان.',
    },
    job: {
      openings: 'شواغر',
      viewRole: 'شوف الوظيفة',
      backToAll: 'كل الوظائف الشاغرة',
      applyNow: 'قدم الآن',
      applyForRole: 'قدم لهذي الوظيفة',
      whatYouWillDo: 'شنو رح تسوي',
      whatWereLookingFor: 'شنو نبحث عنه',
      ogSuffix: 'وظائف داتاكود',
    },
  },
} as const;
